import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CredentialCardView } from '@/components/secrets/CredentialCardView';
import { EmbeddedInfiniteScrollList } from '@/components/ui/embedded-infinite-scroll-list';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { StatusBadge } from '@/components/ui/status-badge';
import { authApi, encryptionKeysApi } from '@/lib/pillarApi';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { runInBackground } from '@/lib/utils';
import {
    hasSuperadminPrivileges,
    type ActiveRoleAssignmentResponse,
    type EncryptionKeyCredentialListItem,
    type EncryptionKeyCredentialRotationAttemptListItem,
    type EncryptionKeyListItem,
    type ListEncryptionKeyCredentialRotationAttemptsResponse,
    type ListEncryptionKeyCredentialsResponse,
    type ListEncryptionKeysResponse,
    type RotateEncryptionKeyResponse,
} from '@anchorbase/pillar-sdk';

const EncryptionKeyOpaqueCursorSchema: z.ZodType<string> = z.string().trim().min(1);
const EncryptionKeyPublicIdentifierParamSchema: z.ZodType<string> = z
    .string()
    .trim()
    .min(1)
    .regex(/^[A-Za-z0-9_-]+$/);

type EncryptionKeyDetailView = 'credentials' | 'credential_rotation_attempts';

type SuperadminAccessState = {
    activeRoleAssignment: ActiveRoleAssignmentResponse | null | undefined;
    isLoading: boolean;
    canManageEncryptionKeys: boolean;
};

function useSuperadminAccessState(): SuperadminAccessState {
    const activeRoleAssignmentQuery = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });
    const activeRoleAssignment = activeRoleAssignmentQuery.data;
    return {
        activeRoleAssignment,
        isLoading: activeRoleAssignmentQuery.isLoading,
        canManageEncryptionKeys: hasSuperadminPrivileges(activeRoleAssignment ?? null),
    };
}

function parseOpaqueCursorParam(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    return EncryptionKeyOpaqueCursorSchema.parse(pageParam);
}

function getErrorMessage(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
}

function SuperadminGate(props: {
    accessState: SuperadminAccessState;
    children: ReactElement;
}): ReactElement {
    if (props.accessState.isLoading) {
        return <div>Loading...</div>;
    }

    if (!props.accessState.canManageEncryptionKeys) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Encryption keys</CardTitle>
                </CardHeader>
                <CardContent>
                    <Alert variant="destructive">
                        <AlertDescription>
                            You do not have permission to manage encryption keys.
                        </AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    return props.children;
}

function formatTimestamp(timestamp: string | null): string {
    if (timestamp === null) {
        return '-';
    }
    return new Date(timestamp).toLocaleString();
}

function EncryptionKeyListRow(props: { item: EncryptionKeyListItem }): ReactElement {
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const item = props.item;
    return (
        <button
            type="button"
            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
            onClick={() => {
                breadcrumbNavigate({
                    source: 'panel',
                    target: {
                        url: `/encryption-keys/${encodeURIComponent(item.secret_name)}`,
                        title: 'Encryption key details',
                    },
                });
            }}
        >
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="break-all font-semibold">{item.secret_name}</h3>
                        <StatusBadge status={item.status} />
                        <Badge variant="outline">{item.encryption_algorithm}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        {item.secret_manager_reference.cloud_vendor.toUpperCase()} /{' '}
                        {item.secret_manager_reference.family} / sequence{' '}
                        {item.secret_manager_reference.sequence_number}
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm md:min-w-[220px]">
                    <div>
                        <div className="text-muted-foreground">Credentials</div>
                        <div className="font-medium">{item.usage_counts.credentials}</div>
                    </div>
                    <div>
                        <div className="text-muted-foreground">Rotation attempts</div>
                        <div className="font-medium">
                            {item.usage_counts.credential_rotation_attempts}
                        </div>
                    </div>
                </div>
            </div>
        </button>
    );
}

export function EncryptionKeysListPage(): ReactElement {
    const accessState = useSuperadminAccessState();
    const queryClient = useQueryClient();
    const [rotationMessage, setRotationMessage] = useState<string | null>(null);
    const [rotationErrorMessage, setRotationErrorMessage] = useState<string | null>(null);
    const queryKeyPrefix: readonly ['encryptionKeys', 'list'] = useMemo(
        () => ['encryptionKeys', 'list'],
        []
    );
    const rotateMutation = useMutation<RotateEncryptionKeyResponse, Error>({
        mutationFn: async () => {
            return await encryptionKeysApi.rotateCurrentKey();
        },
        onMutate: () => {
            setRotationMessage(null);
            setRotationErrorMessage(null);
        },
        onSuccess: (response) => {
            setRotationMessage(`Rotated current key to ${response.secret_name}.`);
            runInBackground(
                queryClient.invalidateQueries({ queryKey: ['encryptionKeys'] }),
                (error) => {
                    setRotationErrorMessage(
                        getErrorMessage(error, 'Failed to refresh encryption keys.')
                    );
                }
            );
        },
        onError: (error) => {
            setRotationErrorMessage(
                getErrorMessage(error, 'Failed to rotate the encryption key.')
            );
        },
    });

    return (
        <SuperadminGate accessState={accessState}>
            <div>
                <div className="mb-6 flex items-center justify-between gap-4">
                    <h1 className="text-3xl font-bold">Encryption keys</h1>
                    <Button
                        type="button"
                        onClick={() => {
                            rotateMutation.mutate();
                        }}
                        disabled={rotateMutation.isPending}
                    >
                        {rotateMutation.isPending ? 'Rotating...' : 'Rotate key'}
                    </Button>
                </div>
                {rotationMessage !== null ? (
                    <Alert className="mb-4">
                        <AlertDescription>{rotationMessage}</AlertDescription>
                    </Alert>
                ) : null}
                {rotationErrorMessage !== null ? (
                    <Alert variant="destructive" className="mb-4">
                        <AlertDescription>{rotationErrorMessage}</AlertDescription>
                    </Alert>
                ) : null}
                <InfiniteScrollList<EncryptionKeyListItem, string, typeof queryKeyPrefix>
                    pageTitle="Encryption keys"
                    cardTitle="Encryption keys"
                    showPageHeader={false}
                    queryPlaceholder="Filter keys..."
                    queryKeyPrefix={queryKeyPrefix}
                    pageSize={50}
                    fetchPage={async (params) => {
                        const response: ListEncryptionKeysResponse =
                            await encryptionKeysApi.listKeys({
                                limit: params.limit,
                                cursor: params.cursor,
                            });
                        return {
                            items: response.encryption_keys,
                            pagination: response.pagination,
                        };
                    }}
                    parseCursorParam={parseOpaqueCursorParam}
                    stateCopy={{
                        loadingLabel: 'Loading encryption keys...',
                        emptyLabel: 'No encryption keys found.',
                        endOfListLabel: 'End of encryption keys.',
                        genericErrorLabel: 'Failed to load encryption keys.',
                    }}
                    renderItems={({ items }) => (
                        <div className="space-y-2">
                            {items.map((item) => (
                                <EncryptionKeyListRow
                                    key={item.secret_name}
                                    item={item}
                                />
                            ))}
                        </div>
                    )}
                />
            </div>
        </SuperadminGate>
    );
}

function CredentialsEncryptedWithKeyView(props: { secretName: string }): ReactElement {
    const queryKeyPrefix: readonly ['encryptionKeys', string, 'credentials'] = useMemo(
        () => ['encryptionKeys', props.secretName, 'credentials'],
        [props.secretName]
    );
    return (
        <EmbeddedInfiniteScrollList<
            EncryptionKeyCredentialListItem,
            string,
            typeof queryKeyPrefix
        >
            queryKeyPrefix={queryKeyPrefix}
            pageSize={50}
            maxHeight="56vh"
            fetchPage={async (params) => {
                const response: ListEncryptionKeyCredentialsResponse =
                    await encryptionKeysApi.listCredentials(props.secretName, {
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                return {
                    items: response.credentials,
                    pagination: response.pagination,
                };
            }}
            parseCursorParam={parseOpaqueCursorParam}
            stateCopy={{
                loadingLabel: 'Loading credentials...',
                emptyLabel: 'No credentials use this key.',
                genericErrorLabel: 'Failed to load credentials.',
            }}
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((credential) => (
                        <CredentialCardView
                            key={credential.credential_uuid}
                            credential={credential}
                            showPublicFields
                        />
                    ))}
                </div>
            )}
        />
    );
}

function CredentialRotationAttemptsEncryptedWithKeyView(props: {
    secretName: string;
}): ReactElement {
    const queryKeyPrefix: readonly [
        'encryptionKeys',
        string,
        'credentialRotationAttempts',
    ] = useMemo(
        () => ['encryptionKeys', props.secretName, 'credentialRotationAttempts'],
        [props.secretName]
    );
    return (
        <EmbeddedInfiniteScrollList<
            EncryptionKeyCredentialRotationAttemptListItem,
            string,
            typeof queryKeyPrefix
        >
            queryKeyPrefix={queryKeyPrefix}
            pageSize={50}
            maxHeight="56vh"
            fetchPage={async (params) => {
                const response: ListEncryptionKeyCredentialRotationAttemptsResponse =
                    await encryptionKeysApi.listCredentialRotationAttempts(props.secretName, {
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                return {
                    items: response.credential_rotation_attempts,
                    pagination: response.pagination,
                };
            }}
            parseCursorParam={parseOpaqueCursorParam}
            stateCopy={{
                loadingLabel: 'Loading rotation attempts...',
                emptyLabel: 'No credential rotation attempts use this key.',
                genericErrorLabel: 'Failed to load credential rotation attempts.',
            }}
            renderItems={({ items }) => (
                <div className="space-y-3">
                    {items.map((attempt) => (
                        <div
                            key={attempt.credential_rotation_attempt_uuid}
                            className="rounded-lg border p-4"
                        >
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                                <h3 className="break-all font-semibold">
                                    {attempt.credential.account_id}
                                </h3>
                                <StatusBadge status={attempt.state} />
                                <Badge variant="outline">{attempt.flow_key}</Badge>
                            </div>
                            <div className="mb-3 grid gap-2 text-sm text-muted-foreground md:grid-cols-2">
                                <div>Created: {formatTimestamp(attempt.created_ts)}</div>
                                <div>Expires: {formatTimestamp(attempt.expires_ts)}</div>
                                <div>Injected: {formatTimestamp(attempt.injected_ts)}</div>
                                <div>Submitted: {formatTimestamp(attempt.submitted_ts)}</div>
                                <div>Promoted: {formatTimestamp(attempt.promoted_ts)}</div>
                                <div>Failed: {formatTimestamp(attempt.failed_ts)}</div>
                            </div>
                            <CredentialCardView
                                credential={attempt.credential}
                                showPublicFields
                                className="bg-white"
                            />
                        </div>
                    ))}
                </div>
            )}
        />
    );
}

function EncryptionKeyDetailViewSelector(props: {
    value: EncryptionKeyDetailView;
    onChange: (view: EncryptionKeyDetailView) => void;
}): ReactElement {
    return (
        <fieldset className="flex flex-wrap gap-3">
            <legend className="sr-only">Encryption key detail view</legend>
            <label className="flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-sm">
                <input
                    type="radio"
                    name="encryption-key-detail-view"
                    value="credentials"
                    checked={props.value === 'credentials'}
                    onChange={() => {
                        props.onChange('credentials');
                    }}
                />
                Credentials
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-sm">
                <input
                    type="radio"
                    name="encryption-key-detail-view"
                    value="credential_rotation_attempts"
                    checked={props.value === 'credential_rotation_attempts'}
                    onChange={() => {
                        props.onChange('credential_rotation_attempts');
                    }}
                />
                Credential rotation attempts
            </label>
        </fieldset>
    );
}

export function EncryptionKeyDetailPage(): ReactElement {
    const accessState = useSuperadminAccessState();
    const params = useParams();
    const [selectedView, setSelectedView] =
        useState<EncryptionKeyDetailView>('credentials');
    const secretNameParseResult = EncryptionKeyPublicIdentifierParamSchema.safeParse(
        params.secretName
    );

    if (!secretNameParseResult.success) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>Encryption key details</CardTitle>
                </CardHeader>
                <CardContent>
                    <Alert variant="destructive">
                        <AlertDescription>Encryption key not found.</AlertDescription>
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    const secretName = secretNameParseResult.data;

    return (
        <SuperadminGate accessState={accessState}>
            <div>
                <div className="mb-6 space-y-2">
                    <h1 className="break-all text-3xl font-bold">Encryption key details</h1>
                    <p className="break-all text-sm text-muted-foreground">{secretName}</p>
                </div>
                <Card>
                    <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <CardTitle>Key usage</CardTitle>
                        <EncryptionKeyDetailViewSelector
                            value={selectedView}
                            onChange={setSelectedView}
                        />
                    </CardHeader>
                    <CardContent>
                        {selectedView === 'credentials' ? (
                            <CredentialsEncryptedWithKeyView secretName={secretName} />
                        ) : (
                            <CredentialRotationAttemptsEncryptedWithKeyView
                                secretName={secretName}
                            />
                        )}
                    </CardContent>
                </Card>
            </div>
        </SuperadminGate>
    );
}
